<template>
  <Dialog
    v-model:visible="visibleProxy"
    class="delete-confirmation-dialog"
    :style="{ width: 'min(440px, calc(100vw - 2rem))' }"
    :modal="true"
    :show-header="false"
    :closable="!loading"
    :close-on-escape="!loading"
  >
    <div class="delete-dialog-content" :dir="direction">
      <div class="delete-dialog-hero">
        <div class="delete-dialog-visual" aria-hidden="true">
          <span class="delete-dialog-icon"><i class="pi pi-trash"></i></span>
        </div>
        <div class="delete-dialog-copy">
          <span class="delete-dialog-kicker">{{ resolvedKicker }}</span>
          <h2>{{ resolvedTitle }}</h2>
          <p>{{ resolvedMessage }}</p>
        </div>
      </div>

      <div class="delete-dialog-note">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span>{{ resolvedNote }}</span>
      </div>
    </div>

    <template #footer>
      <div class="delete-dialog-actions" :dir="direction">
        <Button
          :label="resolvedCancelLabel"
          icon="pi pi-times"
          severity="secondary"
          outlined
          :disabled="loading"
          class="delete-dialog-cancel"
          @click="cancel"
        />
        <Button
          :label="resolvedConfirmLabel"
          icon="pi pi-trash"
          severity="danger"
          :loading="loading"
          :disabled="loading"
          class="delete-dialog-confirm"
          autofocus
          @click="confirm"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  visible: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  lang: { type: String, default: 'en' },
  title: { type: String, default: '' },
  message: { type: String, default: '' },
  note: { type: String, default: '' },
  confirmLabel: { type: String, default: '' },
  cancelLabel: { type: String, default: '' }
});

const emit = defineEmits(['update:visible', 'confirm', 'cancel']);

const isEnglish = computed(() => props.lang === 'en');
const direction = computed(() => (isEnglish.value ? 'ltr' : 'rtl'));
const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});
const resolvedKicker = computed(() => (isEnglish.value ? 'Delete confirmation' : 'تأیید حذف'));
const resolvedTitle = computed(() => props.title || (isEnglish.value ? 'Remove this item?' : 'این مورد حذف شود؟'));
const resolvedMessage = computed(
  () => props.message || (isEnglish.value ? 'Please review your choice before continuing.' : 'لطفاً قبل از ادامه، انتخاب خود را بررسی کنید.')
);
const resolvedNote = computed(
  () => props.note || (isEnglish.value ? 'This action cannot be undone.' : 'این عملیات قابل بازگشت نیست.')
);
const resolvedConfirmLabel = computed(() => props.confirmLabel || (isEnglish.value ? 'Delete' : 'حذف کردن'));
const resolvedCancelLabel = computed(() => props.cancelLabel || (isEnglish.value ? 'Cancel' : 'انصراف'));

const cancel = () => {
  if (props.loading) return;
  visibleProxy.value = false;
  emit('cancel');
};

const confirm = () => {
  if (props.loading) return;
  emit('confirm');
};
</script>

<style scoped>
.delete-confirmation-dialog {
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--fm-danger) 16%, var(--fm-border));
  border-radius: 1.35rem;
  background: var(--fm-surface);
  box-shadow: 0 24px 80px rgb(15 23 42 / 22%);
}

.delete-confirmation-dialog :deep(.p-dialog-content) {
  padding: 0;
  background: transparent;
}

.delete-confirmation-dialog :deep(.p-dialog-footer) {
  padding: 0 1.5rem 1.5rem;
  border: 0;
  background: transparent;
}

.delete-dialog-content {
  padding: 1.6rem 1.5rem 1rem;
}

.delete-dialog-hero {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.delete-dialog-visual {
  position: relative;
  flex: 0 0 auto;
  width: 4.2rem;
  height: 4.2rem;
}

.delete-dialog-visual::after {
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  bottom: 0.25rem;
  left: 0.25rem;
  border: 1px solid color-mix(in srgb, var(--fm-danger) 20%, transparent);
  border-radius: 1.45rem;
  content: '';
  transform: rotate(8deg);
}

.delete-dialog-icon {
  position: relative;
  z-index: 1;
  display: grid;
  width: 3.7rem;
  height: 3.7rem;
  border: 7px solid color-mix(in srgb, var(--fm-danger) 11%, var(--fm-surface));
  border-radius: 1.25rem;
  color: #fff;
  background: linear-gradient(145deg, color-mix(in srgb, var(--fm-danger) 76%, #fff), var(--fm-danger));
  box-shadow: 0 10px 22px color-mix(in srgb, var(--fm-danger) 22%, transparent);
  place-items: center;
}

.delete-dialog-icon i {
  font-size: 1.35rem;
}

.delete-dialog-copy {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 0.15rem;
}

.delete-dialog-kicker {
  display: block;
  margin-bottom: 0.35rem;
  color: var(--fm-danger);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.delete-dialog-copy h2 {
  margin: 0;
  color: var(--fm-text-strong);
  font-size: 1.3rem;
  line-height: 1.35;
}

.delete-dialog-copy p {
  margin: 0.45rem 0 0;
  color: var(--fm-text-muted);
  font-size: 0.88rem;
  line-height: 1.8;
}

.delete-dialog-note {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 1.35rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid color-mix(in srgb, var(--fm-danger) 18%, var(--fm-border));
  border-radius: var(--fm-radius-md);
  color: color-mix(in srgb, var(--fm-danger) 78%, var(--fm-text-strong));
  background: color-mix(in srgb, var(--fm-danger) 7%, var(--fm-surface));
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.6;
}

.delete-dialog-note i {
  flex: 0 0 auto;
  color: var(--fm-danger);
  font-size: 1rem;
}

.delete-dialog-actions {
  display: flex;
  gap: 0.75rem;
}

.delete-dialog-actions .p-button {
  min-height: 2.8rem;
  flex: 1 1 0;
}

.delete-dialog-confirm {
  box-shadow: 0 8px 18px color-mix(in srgb, var(--fm-danger) 20%, transparent);
}

.delete-dialog-confirm:not(:disabled):hover {
  box-shadow: 0 10px 22px color-mix(in srgb, var(--fm-danger) 30%, transparent);
}

@media (max-width: 480px) {
  .delete-confirmation-dialog :deep(.p-dialog-footer) {
    padding: 0 1rem 1rem;
  }

  .delete-dialog-content {
    padding: 1.25rem 1rem 0.9rem;
  }

  .delete-dialog-hero {
    gap: 0.75rem;
  }

  .delete-dialog-visual {
    width: 3.65rem;
    height: 3.65rem;
  }

  .delete-dialog-icon {
    width: 3.2rem;
    height: 3.2rem;
    border-width: 6px;
    border-radius: 1.1rem;
  }

  .delete-dialog-copy h2 {
    font-size: 1.15rem;
  }

  .delete-dialog-copy p {
    font-size: 0.82rem;
  }

  .delete-dialog-actions {
    flex-direction: column-reverse;
  }

  .delete-dialog-actions .p-button {
    width: 100%;
  }
}
</style>
